'use client';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import {
  CarouselProvider,
  useCarouselContext,
} from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselProgress } from '@eliadmin/core/src/components/ui/carousel-progress';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';

interface ProductsCarouselProps {
  products: Product[];
  locale: string;
  title: string;
  badge?: {
    color: string;
    name: string;
    textColor: string;
  };
  className?: {
    section?: string;
  };
}

const CAROUSEL_OPTIONS = { align: 'start' as const };

function ProductsCarouselInner({
  products,
  locale,
  title,
  badge,
  className,
}: ProductsCarouselProps) {
  const { selectedIndex, slideCount } = useCarouselContext()!;
  const me = useCustomerStore(state => state.me);
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const { data } = useQuery({
    queryKey: ['HOMEPAGE_PRODUCTS', locale, me?.customer.id ?? null, me?.authToken ?? null],
    queryFn: () =>
      fetchProducts({
        locale,
        authToken: me?.authToken,
        query: { results: 10 },
      }),
    enabled: isInitialized && Boolean(me?.authToken),
  });
  const displayedProducts = data?.data ?? products;

  return (
    <section
      className={cn(
        'custom-section',
        'generic-gap',
        'py-6 sm:py-8 md:py-10 lg:py-12',
        className?.section,
      )}
    >
      <div className="flex flex-col items-center justify-center gap-x-3 gap-y-2 sm:flex-row sm:flex-wrap">
        <Heading title={title} center color="black" />
        {badge && (
          <p
            className="flex h-[28px] items-center justify-center rounded-full px-2 text-xs font-medium sm:h-[32px] sm:px-3 sm:text-sm lg:h-[34px]"
            style={{ backgroundColor: badge.color, color: badge.textColor }}
          >
            {badge.name}
          </p>
        )}
      </div>
      <div className={cn('custom-container')}>
        <Carousel
          options={CAROUSEL_OPTIONS}
          className={{
            wrapper: 'w-full',
            container: 'w-full gap-3 sm:gap-[10px]',
            main: 'w-full',
            slide:
              'w-full max-w-[173px] rounded-[20px] border bg-white sm:max-w-[195px] md:max-w-[220px] lg:max-w-[272px]',
          }}
          slides={displayedProducts.map(product => (
            <ProductCard
              product={product}
              key={product.slug}
              className={{ main: 'bg-white' }}
              border={false}
            />
          ))}
        />
      </div>
      <div className="flex w-full max-w-[450px] items-center justify-center">
        <CarouselProgress selectedIndex={selectedIndex} totalSlides={slideCount} />
      </div>
    </section>
  );
}

export function ProductsCarousel(props: ProductsCarouselProps) {
  return (
    <CarouselProvider>
      <ProductsCarouselInner {...props} />
    </CarouselProvider>
  );
}
